import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { AudioMaster } from '../../components/AudioMaster';
import { StatusTag } from '../../components/StatusTag';
import { VideoStage } from '../../components/VideoStage';
import { clientStatusLabel, deliverableStage, isReviewable, metaLabel } from '../../utils/deliverables';
type Panel = 'idle' | 'confirm' | 'approved';
const EASE = [0.23, 1, 0.32, 1] as const;
export function EpisodeReview() {
  const {
    activeDeliverable,
    reviewDeliverables,
    approveDeliverable,
    requestChanges,
    goClient,
    notify,
    canApprove,
    canComment,
    projectComplete
  } = usePortal();
  const [panel, setPanel] = useState<Panel>('idle');
  const [composerOpen, setComposerOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [timecode, setTimecode] = useState('');
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const index = reviewDeliverables.filter(entry => deliverableStage(entry.status) === deliverableStage(item.status)).findIndex(entry => entry.id === item.id);
  const number = index >= 0 ? String(index + 1).padStart(2, '0') : '';
  const inReview = isReviewable(item.status);
  const settled = item.status === 'Approved' || item.status === 'Delivered' || item.status === 'Ready for Download';
  const changesRequested = item.status === 'Changes Requested';
  const showVideo = item.type === 'Video' || item.type === 'Other' || item.type === 'Folder';
  const showAudio = item.type === 'Audio' || Boolean(item.audioUrl);

  /** Changes stay open for as long as this version is the current cut. */
  const canAddChanges = canComment && !projectComplete && !settled && (inReview || changesRequested);
  const currentChanges = item.changeRequests.filter(request => request.version === item.version);
  function confirmApproval() {
    approveDeliverable(item.id);
    setPanel('approved');
    setComposerOpen(false);
    notify('Portal notified');
  }
  function addChange(event: React.FormEvent) {
    event.preventDefault();
    const body = changeNote.trim();
    if (!body) return;
    requestChanges(item.id, body, timecode);
    setChangeNote('');
    setTimecode('');
    notify('Change added · Portal has been notified');
  }
  return <div data-id="element-466" data-mp="11c6qkpnezp9v">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-467" data-mp="11et0gvxs2gv9">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-468" data-mp="1fc97a8jzkcnj" />
        All Deliverables
      </button>

      <header className="mt-10 border-b border-rule pb-10" data-id="element-469" data-mp="3h3m7wlex7yds">
        <p className="label text-mute" data-id="element-470" data-mp="3h3upgqlznrvb">{metaLabel(item)}</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-471" data-mp="39kd0uytw0brw">
          {number && `${number} — `}
          {item.name.toUpperCase()}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-472" data-mp="2zu0jffhdcypz">
          <StatusTag status={item.status} audience="review" data-id="element-473" data-mp="2zrxk24ubtt8l" />
          {item.version && <p className="label m-0 text-mute" data-id="element-474" data-mp="2ux0cz3glxtyc">Version {item.version}</p>}
          {item.versionDate !== '—' && <p className="label m-0 text-mute" data-id="element-475" data-mp="3si64y4g8yrqv">{item.versionDate}</p>}
          {item.runtime !== '—' && <p className="label m-0 text-mute" data-id="element-476" data-mp="3nke1qea5jj7d">Runtime {item.runtime}</p>}
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-477" data-mp="3ij6x2o3b304x">
        <div className="lg:col-span-8" data-id="element-478" data-mp="3eiejxjrbcb3d">
          {showVideo && <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-479" data-mp="275uwcm2uexmz">
                <h2 className="label m-0 text-graphite" data-id="element-480" data-mp="232ydqvdaz1j1">Latest Review Version</h2>
                <p className="label-sm m-0 text-mute" data-id="element-481" data-mp="3r0fkfb7ky6f3">
                  {item.version ? `${item.version} · ` : ''}Plays inside Portal
                </p>
              </div>
              <div className="mt-6" data-id="element-482" data-mp="3h2jeqyxew3od">
                <VideoStage driveUrl={item.driveUrl} label={`${item.name} — ${item.version || 'Review copy'}`} runtime={item.runtime} format={item.videoFormat} data-id="element-483" data-mp="3h3kn2vn1mc2e" />
              </div>
            </>}

          {!showVideo && !showAudio && <div className="border border-rule px-7 py-9" data-id="element-484" data-mp="iit686km5l26">
              <p className="label m-0 text-graphite" data-id="element-485" data-mp="4k30y3nn4f2q">Latest Review Version</p>
              <p className="mt-5 font-display text-2xl" data-id="element-486" data-mp="8m6mjl40x01q">{item.name}</p>
              {settled ? <p className="label mt-6 text-mute" data-id="element-487" data-mp="2patztanc7xpa">{clientStatusLabel(item.status)}</p> : <button type="button" onClick={() => notify('Simulated · would open in Google Drive')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-488" data-mp="2td74dvqclpkh">
                  Open for Review
                  <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-489" data-mp="2vp99cjpc8w2n" />
                </button>}
            </div>}

          <div className="mt-12" data-id="element-490" data-mp="233zldhyebppo">
            <AnimatePresence mode="wait" initial={false} data-id="element-491" data-mp="230u06dsw17z8">
              {panel === 'approved' || settled && panel === 'idle' ? <motion.div key="approved" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-492" data-mp="2ct9gz7sg7qij">
                  <p className="flex items-center gap-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-493" data-mp="1fdvijct17tzq">
                    {item.status === 'Approved' ? 'Approved' : item.status}
                    <CheckIcon className="h-6 w-6" strokeWidth={1} data-id="element-494" data-mp="292uphdntn7b5" />
                  </p>
                  <p className="label mt-5 text-mute" data-id="element-495" data-mp="2dtt6kfgmw9fg">
                    {item.status === 'Approved' ? `${item.version ? `${item.version} approved · ` : ''}Portal has been notified.` : 'Your final files are ready to download.'}
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-496" data-mp="1m4zqqiu58y6e">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-497" data-mp="36hf9rmly9c1s" />
                  </button>
                </motion.div> : projectComplete ? <motion.div key="complete" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-498" data-mp="uw6erww58nhr">
                  <p className="font-display text-3xl leading-none sm:text-4xl" data-id="element-499" data-mp="h4i1hqa5rsz7">Delivered</p>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-500" data-mp="c3m3ftchn11p">
                    This project is complete. Final files are available to download.
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-501" data-mp="27izchy2ssc4">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-502" data-mp="15h7tdgrvkd6l" />
                  </button>
                </motion.div> : panel === 'confirm' ? <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-503" data-mp="10m1vrkp41a2o">
                  <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-504" data-mp="dqbc83ys7mdi">
                    Approve {item.version ? `version ${item.version}` : item.name}?
                  </h2>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-505" data-mp="2805bp7cigp17">
                    Portal will be notified and will prepare your final deliverables.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-506" data-mp="e59aj60dsb5">
                    <button type="button" onClick={confirmApproval} className="label border border-ink bg-ink px-7 py-4 text-paper transition-colors duration-150 ease-out hover:bg-charcoal" data-id="element-507" data-mp="3slbv4uxbtfui">
                      Confirm Approval
                    </button>
                    <button type="button" onClick={() => setPanel('idle')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-508" data-mp="f54d7gvwfsoe">
                      Cancel
                    </button>
                  </div>
                </motion.div> : canApprove || canAddChanges ? <motion.div key="actions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} data-id="element-509" data-mp="2pxfafqyy0jn7">
                  <div className="flex flex-wrap gap-5" data-id="element-510" data-mp="1vc8zj15zc16a">
                    {canApprove && !projectComplete && !settled && <button type="button" onClick={() => setPanel('confirm')} className="label flex-1 border border-ink bg-ink px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-charcoal sm:flex-none" data-id="element-511" data-mp="1rbgpi5pa9s8y">
                        Approve
                      </button>}
                    {canAddChanges && <button type="button" onClick={() => setComposerOpen(true)} className="label flex-1 border border-ink px-8 py-5 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper sm:flex-none" data-id="element-512" data-mp="25ay8u5jt8dax">
                        {currentChanges.length > 0 ? 'Add Another Change' : 'Request Changes'}
                      </button>}
                  </div>

                  {changesRequested && <p className="label mt-6 text-mute" data-id="element-513" data-mp="3f7wq43e48h0h">
                      Changes requested · Portal has been notified
                    </p>}
                </motion.div> : inReview ? <motion.p key="viewonly" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-514" data-mp="3o94dhqf3fxfs">
                  Your access is view only · approvals sit with your team&rsquo;s approver
                </motion.p> : <motion.p key="editing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-515" data-mp="ft1ee921rh32">
                  This is with Portal · nothing to review yet
                </motion.p>}
            </AnimatePresence>
          </div>

          {composerOpen && canAddChanges && <form onSubmit={addChange} className="mt-8 border border-ink px-7 py-9 sm:px-10" data-id="element-516" data-mp="2vk6ugxggwae2">
              <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-517" data-mp="2qlviag2p9nso">
                What would you like us to change?
              </h2>
              <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-start" data-id="element-518" data-mp="34kuzj2nvv0et">
                <label className="sm:w-32" data-id="element-519" data-mp="2znlcftnyv0u5">
                  <span className="label block text-graphite" data-id="element-520" data-mp="2zkgwjz152gnn">Timecode</span>
                  <input value={timecode} onChange={event => setTimecode(event.target.value)} placeholder="e.g. 00:17" inputMode="numeric" className="mt-4 w-full border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-521" data-mp="1a7lvhqe3w73y" />
                </label>
                <label className="flex-1" data-id="element-522" data-mp="3c4dxcf1scy7g">
                  <span className="label block text-graphite" data-id="element-523" data-mp="3c3cjk2tdxxm1">Change Request</span>
                  <textarea value={changeNote} onChange={event => setChangeNote(event.target.value)} rows={3} placeholder="Describe the change required…" className="mt-4 w-full resize-none border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-524" data-mp="2y4cyt68zu2v5" />
                </label>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-525" data-mp="rdna65bgb40q">
                <button type="submit" className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-526" data-mp="2nr2t9dvh4pw">
                  Add Change
                </button>
                <button type="button" onClick={() => setComposerOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-527" data-mp="3oxbras6ads0k">
                  Done
                </button>
              </div>
            </form>}

          {currentChanges.length > 0 && <section aria-labelledby="requested-changes" className="mt-16" data-id="element-528" data-mp="365j093as2gvh">
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-529" data-mp="316fs39zi3uja">
                <h2 id="requested-changes" className="label m-0 text-graphite" data-id="element-530" data-mp="2w5ic941yrcky">
                  Requested Changes{item.version ? ` · ${item.version}` : ''}
                </h2>
                <p className="label-sm m-0 text-mute" data-id="element-531" data-mp="17ngwgr49gc6t">
                  {currentChanges.length} change{currentChanges.length === 1 ? '' : 's'}
                </p>
              </div>
              <ul className="m-0 list-none p-0" data-id="element-532" data-mp="1gnlzyq6c2qfp">
                {currentChanges.map(request => <li key={request.id} className="border-b border-rule py-6" data-id="element-533" data-mp="1lmgkbwjy8n1x">
                    <div className="flex flex-wrap items-baseline gap-3" data-id="element-534" data-mp="1lkcirl9iw04t">
                      <span className="label text-ink" data-id="element-535" data-mp="o0j465t1ktt5">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      {request.resolved && <span className="label-sm text-mute" data-id="element-536" data-mp="xvb3be29voq1">Completed</span>}
                    </div>
                    <p className="mt-2 max-w-2xl font-display text-xl leading-snug text-charcoal" data-id="element-537" data-mp="12pnmro3r101s">
                      {request.body}
                    </p>
                    <p className="label-sm mt-3 text-mute" data-id="element-538" data-mp="a68mg8fgpzwl">Sent {request.time}</p>
                  </li>)}
              </ul>
            </section>}

          {item.versions.some(entry => entry.visibleToClient) && <section aria-labelledby="previous-versions" className="mt-16" data-id="element-539" data-mp="bwnjl58al4yp">
              <h2 id="previous-versions" className="label border-b border-rule pb-4 text-graphite" data-id="element-540" data-mp="byrlxiqjlvwv">
                Previous Versions
              </h2>
              <ul className="m-0 list-none p-0" data-id="element-541" data-mp="3ffgjif85dvae">
                {item.versions.filter(entry => entry.visibleToClient).map(entry => <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule py-5" data-id="element-542" data-mp="3pd4p607akn7c">
                      <span className="flex items-baseline gap-5" data-id="element-543" data-mp="14jrhkq8ywj0z">
                        <span className="font-display text-2xl leading-none" data-id="element-544" data-mp="zn1327ct9vho">{entry.version}</span>
                        <span className="label-sm text-mute" data-id="element-545" data-mp="2hjl9whgyj56y">
                          {entry.label} · {entry.date}
                          {entry.changeCount ? ` · ${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}` : ''}
                        </span>
                      </span>
                      <button type="button" onClick={() => notify(`Simulated · would play ${entry.version}`)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-546" data-mp="2dkdkojwrx0ua">
                        Watch
                      </button>
                    </li>)}
              </ul>
            </section>}

          {showAudio && <section aria-labelledby="audio-review" className="mt-16" data-id="element-547" data-mp="3k5y0os0rhru0">
              <h2 id="audio-review" className="label border-b border-rule pb-4 text-graphite" data-id="element-548" data-mp="3oxoc71bfqln0">
                Audio Review
              </h2>
              <AudioMaster label={`${item.name} — Audio ${item.version}`} driveUrl={item.audioUrl} data-id="element-549" data-mp="1owhspkhwujjg" />
            </section>}

        </div>

        <aside className="lg:col-span-4 lg:border-l lg:border-rule lg:pl-12" data-id="element-550" data-mp="1f5n5acsxhdwf">
          <h2 className="label border-b border-rule pb-4 text-graphite" data-id="element-551" data-mp="1a7lm7rvi5eky">How Review Works</h2>
          <ol className="m-0 list-none p-0" data-id="element-552" data-mp="27sztereqltru">
            {[{
            step: '01',
            title: 'Watch',
            body: 'The latest version plays here inside Portal.'
          }, {
            step: '02',
            title: 'Respond',
            body: 'Add as many timecoded changes as you need.'
          }, {
            step: '03',
            title: 'Approve',
            body: 'Confirm the version once you are happy.'
          }, {
            step: '04',
            title: 'Download',
            body: 'Final deliverables move to Files after approval.'
          }].map(step => <li key={step.step} className="border-b border-rule py-6" data-id="element-553" data-mp="2u412xpeguzy1">
                <div className="flex items-baseline gap-4" data-id="element-554" data-mp="2y535og7ldu4r">
                  <span className="label text-mute" data-id="element-555" data-mp="3334qb5ko21ic">{step.step}</span>
                  <span className="font-display text-xl leading-none" data-id="element-556" data-mp="331tmj5r80tr8">{step.title}</span>
                </div>
                <p className="mt-3 font-display text-base leading-snug text-graphite" data-id="element-557" data-mp="37vxr5id0kpp5">{step.body}</p>
              </li>)}
          </ol>
          <p className="label-sm mt-7 leading-relaxed text-mute" data-id="element-558" data-mp="3hr99sbrjmh26">
            Review copies stream from Google Drive · finals are delivered in Files
          </p>
        </aside>
      </div>
    </div>;
}